import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import CompanyProfileModal from "../Company/ProfileModal";
import EmployeeRegistrationModal from "../Company/EmployeeRegistrationModal";
import { apiService } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const Dashboard = () => {
  const { state } = useAuth();
  const token = state.token;
  const userId = state.user?.id;
  
  const [profile, setProfile] = useState(null);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);

  const [tasks, setTasks] = useState([
    { id: 1, title: "Review Q1 financials", dueDate: "Today", priority: "high", completed: false },
    { id: 2, title: "HR meeting - new hires", dueDate: "Tomorrow", priority: "medium", completed: false },
    { id: 3, title: "Update payroll system", dueDate: "Feb 28", priority: "high", completed: false },
    { id: 4, title: "Team performance reviews", dueDate: "Mar 5", priority: "medium", completed: true },
  ]);
  
  // Activity data with real-time updates
  const [activities, setActivities] = useState([
    { id: 1, user: "Sarah Wong", action: "approved", item: "payroll for Tech dept", time: "2 mins ago" },
    { id: 2, user: "Alex Johnson", action: "updated", item: "employee benefits policy", time: "1 hour ago" },
    { id: 3, user: "Maria Garcia", action: "created", item: "new accounts report", time: "3 hours ago" },
  ]);
  
  // Update activity feed with new items periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const newActivity = {
        id: activities.length + 1,
        user: ["David Lee", "Emma Chen", "Michael Singh", "Priya Patel"][Math.floor(Math.random() * 4)],
        action: ["updated", "created", "approved", "reviewed"][Math.floor(Math.random() * 4)],
        item: ["employee record", "expense report", "timesheet", "department budget"][Math.floor(Math.random() * 4)],
        time: "just now"
      };
      
      setActivities(prev => [newActivity, ...prev.slice(0, 4)]);
    }, 45000); // Add new activity every 45 seconds
    
    return () => clearInterval(interval);
  }, [activities]);
    
  
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0, 
      transition: { 
        delay: i * 0.1,
        duration: 0.4,
        ease: "easeOut" 
      } 
    })
  };

  useEffect(() => {
    if (!token || !userId) return;
    const fetchProfile = async () => {
      try {
        const response = await apiService.get(`/api/profile/company/${userId}`, token);
        console.log("Fetched profile:", response.data);
        setProfile(response.data);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, [token, userId]);

  return (
    <div className="flex min-h-screen bg-gray-50 font-source-sans-3">
      
      {/* Main content */}
      <div className={`flex-1 transition-all duration-300`}>        
        {/* Dashboard content */}
        <main className="px-4 py-8">
          {/* Welcome section */}
          {profile ? ( 
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-primary-800 source-sans-3">Welcome back, {profile.name}</h2>
                  {/* <p className="text-primary-600 mt-1">
                    Registration: {profile.registrationNumber} | Industry: {profile.industryType}
                  </p> */}
                  <p className="text-primary-600 mt-1 source-sans-3">Here's what's happening with your business today.</p>
                </div>
                <div className="mt-4 md:mt-0">
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-primary-600 text-white px-4 source-sans-3 py-2 rounded-lg shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    Generate Report
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ) : (
            <div>Loading profile...</div>
          )}
          
          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { title: "Total Employees", value: profile ? profile.employees?.[0]?.number : "Loading...", change: "+12%", icon: (
                <svg className="w-6 h-6 text-primary-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) },
              { title: "Monthly Payroll", value: "$186,429", change: "+5.2%", icon: (
                <svg className="w-6 h-6 text-emerald-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) },
              { title: "Pending Approvals", value: "12", change: "-3", icon: (
                <svg className="w-6 h-6 text-amber-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) },
              { title: "Open Tickets", value: "8", change: "+2", icon: (
                <svg className="w-6 h-6 text-rose-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              ) }
            ].map((stat, i) => (
              <motion.div
                key={stat.title}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      i === 0 ? 'bg-primary-600' : 
                      i === 1 ? 'bg-emerald-600' : 
                      i === 2 ? 'bg-amber-600' : 
                      'bg-rose-600'
                    }`}>
                      {stat.icon}
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-gray-500 font-medium source-sans-3">{stat.title}</p>
                      <div className="flex items-baseline">
                        <p className="text-2xl font-bold text-gray-900 source-sans-3">{stat.value}</p>
                        <span className={`ml-2 text-xs source-sans-3 font-medium ${
                          stat.change.startsWith('+') ? 'text-green-600' :
                          stat.change.startsWith('-') ? 'text-red-600' :
                          'text-gray-500'
                        }`}>
                          {stat.change}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`h-1 ${
                  i === 0 ? 'bg-primary-600' : 
                  i === 1 ? 'bg-emerald-600' : 
                  i === 2 ? 'bg-amber-600' : 
                  'bg-rose-600'
                }`}></div>
              </motion.div>
            ))}
          </div>
          
          {/* Main dashboard grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tasks panel */}
            <motion.div 
              variants={cardVariants}
              custom={0}
              initial="hidden"
              animate="visible"
              className="bg-white rounded-xl shadow-sm overflow-hidden lg:col-span-2"
            >
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-primary-800 source-sans-3">Pending Tasks</h3>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-primary-600 hover:text-primary-800 text-sm font-medium source-sans-3"
                  >
                    View All
                  </motion.button>
                </div>
              </div>
              
              <div className="p-5">
                {tasks.map((task, i) => (
                  <motion.div 
                    key={task.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + (i * 0.1) }}
                    className={`flex items-center py-3 ${i !== tasks.length - 1 ? 'border-b border-gray-100' : ''}`}
                  >
                    <input 
                      type="checkbox" 
                      checked={task.completed}
                      onChange={() => {
                        const updatedTasks = [...tasks];
                        updatedTasks[i].completed = !task.completed;
                        setTasks(updatedTasks);
                      }}
                      className="form-checkbox h-5 w-5 text-primary-600 source-sans-3 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <div className="ml-3 flex-1">
                      <p className={`font-medium source-sans-3 ${task.completed ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                        {task.title}
                      </p>
                      <div className="flex items-center mt-1">
                        <span className="text-xs source-sans-3 text-gray-500">Due: {task.dueDate}</span>
                        <span className={`ml-3 px-2 source-sans-3 py-0.5 rounded-full text-xs font-medium ${
                          task.priority === 'high' ? 'bg-red-100 text-red-800' :
                          task.priority === 'medium' ? 'bg-amber-100 text-amber-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                        </span>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-primary-600 source-sans-3">
                      <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                      </svg>
                    </button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Activity Feed */}
            <motion.div 
              variants={cardVariants}
              custom={1}
              initial="hidden"
              animate="visible"
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className="p-5 border-b border-gray-100">
                <h3 className="font-bold text-lg text-primary-800 source-sans-3">Recent Activity</h3>
              </div>
              
              <div className="p-5">
                {activities.map((activity, i) => (
                  <motion.div 
                    key={activity.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + (i * 0.1) }}
                    className={`py-3 ${i !== activities.length - 1 ? 'border-b border-gray-100' : ''}`}
                  >
                    <div className="flex">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center source-sans-3 text-white ${
                        activity.action === 'created' ? 'bg-green-500' :
                        activity.action === 'updated' ? 'bg-blue-500' :
                        activity.action === 'approved' ? 'bg-primary-600' :
                        'bg-amber-500'
                      }`}>
                        {activity.user.charAt(0)}
                      </div>
                      <div className="ml-3">
                            <p className="text-sm source-sans-3">
                                <span className="font-medium text-gray-900 source-sans-3">{activity.user}</span>
                                {' '}
                                <span className="text-gray-600 source-sans-3">{activity.action}</span>
                                {' '}
                                <span className="font-medium text-gray-900 source-sans-3">{activity.item}</span>
                            </p>
                            <p className="text-xs text-gray-500 source-sans-3 mt-1">{activity.time}</p>
                        </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Calendar widget */}
            <motion.div 
              variants={cardVariants}
              custom={2}
              initial="hidden"
              animate="visible"
              className="bg-white rounded-xl shadow-sm overflow-hidden lg:col-span-3"
            >
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-primary-800 source-sans-3">Upcoming Payroll</h3>
                  <div className="flex space-x-2">
                    <button className="text-sm text-primary-600 source-sans-3 hover:text-primary-800 font-medium">This Month</button>
                    <button className="text-sm text-gray-500 source-sans-3 hover:text-primary-800 font-medium">Next Month</button>
                  </div>
                </div>
              </div>
              
              <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { id: 1, dept: "Engineering", dueDate: "Feb 25, 2025", amount: "$58,420", status: "processing", employees: 42 },
                  { id: 2, dept: "Marketing", dueDate: "Feb 27, 2025", amount: "$32,180", status: "pending", employees: 28 },
                  { id: 3, dept: "Operations", dueDate: "Mar 1, 2025", amount: "$45,750", status: "approved", employees: 36 }
                ].map((payroll, i) => (
                  <motion.div
                    key={payroll.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + (i * 0.1) }}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-semibold source-sans-3 text-primary-800">{payroll.dept} Dept</h4>
                      <span className={`px-2 py-1 rounded-full source-sans-3 text-xs font-medium ${
                        payroll.status === 'approved' ? 'bg-green-100 text-green-800' :
                        payroll.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {payroll.status.charAt(0).toUpperCase() + payroll.status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-sm source-sans-3 text-gray-500">Due Date</p>
                        <p className="font-medium source-sans-3">{payroll.dueDate}</p>
                        <p className="text-sm source-sans-3 text-gray-500 mt-2">{payroll.employees} Employees</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm source-sans-3 text-gray-500">Total Amount</p>
                        <p className="text-xl source-sans-3 font-bold text-primary-800">{payroll.amount}</p>
                      </div>
                    </div>
                    <button className="mt-4 w-full py-2 text-sm source-sans-3 font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors">
                      Review Details
                    </button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Quick access tiles */}
            <motion.div 
            variants={cardVariants}
            custom={3}
            initial="hidden"
            animate="visible"
            className="bg-white rounded-xl shadow-sm overflow-hidden lg:col-span-3 mt-6"
          >
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-bold text-lg text-primary-800 source-sans-3">Quick Actions</h3>
            </div>
            
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { 
                  title: "New Employee", 
                  icon: (
                    <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  ), 
                  color: "primary",
                  action: () => setIsEmployeeModalOpen(true) // Add action to open modal
                },
                { title: "Process Payroll", icon: (
                  <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ), color: "emerald" },
                { title: "Generate Reports", icon: (
                  <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ), color: "amber" },
                { title: "Help Center", icon: (
                  <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ), color: "rose" }
              ].map((action, i) => (
                <motion.button
                  key={action.title}
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={action.action} // Use the action if provided
                  className={`flex flex-col items-center justify-center p-6 rounded-lg border source-sans-3 border-gray-200 hover:shadow-md transition-all ${
                    action.color === 'primary' ? 'hover:bg-primary-50 text-primary-700' :
                    action.color === 'emerald' ? 'hover:bg-emerald-50 text-emerald-700' :
                    action.color === 'amber' ? 'hover:bg-amber-50 text-amber-700' :
                    'hover:bg-rose-50 text-rose-700'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                    action.color === 'primary' ? 'bg-primary-100' :
                    action.color === 'emerald' ? 'bg-emerald-100' :
                    action.color === 'amber' ? 'bg-amber-100' :
                    'bg-rose-100'
                  }`}>
                    {action.icon}
                  </div>
                  <span className="font-medium">{action.title}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
          </div>
        </main>
      </div>
      <CompanyProfileModal />
      <EmployeeRegistrationModal 
        isOpen={isEmployeeModalOpen} 
        onClose={() => setIsEmployeeModalOpen(false)} 
      />
    </div>
  );
};

export default Dashboard;